<template>
  <SignatureDialogLayout title="Gerencie seu plano" :can-close="canClose" @close="handleClose">
    <div class="current-signature">
      <div class="section-header mb-2">Plano Atual</div>
      <div class="current-plan">
        <div class="detail">
          <VIcon v-if="status === 'pending'" color="#E8A43D">mdi-clock-time-five-outline</VIcon>
          <VIcon v-if="status === 'canceled'" color="#DA4343">mdi-close-circle-outline</VIcon>
          <div class="icon" v-if="status === 'ok'">
            <VIcon color="#FFF" size="large">mdi-check</VIcon>
          </div>

          <p>{{ name }}</p>
          <div class="price" v-if="price">R$ {{ price.toFixed(2) }}<span>{{ cycle === 'YEARLY' ? '/ano' : '/mês' }}</span></div>
          <div class="price" v-else-if="isLoading">Carregando...</div>
          <div class="price" v-else>Grátis</div>
        </div>
        <div class="divider"></div>

        <div v-if="paymentMethod !== 'FREE'" class="payment-method my-4">
          <div style="flex-grow: 1">Forma de pagamento</div>

          <div v-if="paymentMethod === 'CREDIT_CARD'" class="method">
            <div>
              <VIcon color="#8D97A0">mdi-credit-card-outline</VIcon>
            </div>
            <div class="description">•••• •••• •••• {{ creditCardFinalNumber }}</div>
          </div>

          <div v-if="paymentMethod === 'BOLETO'" class="method">
            <div>
              <VIcon color="#8D97A0">mdi-barcode-scan</VIcon>
            </div>
            <div class="description">Boleto bancário</div>
          </div>

          <div v-if="paymentMethod === 'PIX'" class="method">
            <div>
              <VIcon color="#8D97A0">mdi-qrcode</VIcon>
            </div>
            <div class="description">Pix</div>
          </div>
        </div>

        <VBtn v-if="!isLoading && !['personalizado'].includes(name?.toLowerCase())" depressed style="width: 100%" @click="$emit('change-plan')"
          >Alterar Plano</VBtn
        >
      </div>

      <p class="mt-3">
        Seu plano será renovado automaticamente. É possível alterar a qualquer momento.
        <a href="#" @click="handleWhatsappClick('Tenho algumas dúvidas sobre as faturas')">Dúvidas? Clique aqui e fale conosco</a>
      </p>

      <div class="section-header mt-6 mb-2">Últimas Faturas</div>

      <div class="invoices">
        <template v-if="invoices.length">
          <Invoice v-for="invoice in invoices" :key="invoice.invoiceUrl" :invoice="invoice" />
        </template>
        <div v-else class="empty">Você não possui faturas em aberto.</div>
      </div>
      <SignatureSafePaymentSection />
    </div>
  </SignatureDialogLayout>
</template>
<script>
import { mapActions, mapState } from 'vuex';
import OrganizationSignatureService from '@/services/api/organizationSignature';
import SignatureDialogLayout from '../SignatureDialogLayout';
import SignatureSafePaymentSection from '../SignatureSafePaymentSection';
import Invoice from './Invoice';

export default {
  name: 'CurrentSignature',
  components: { SignatureDialogLayout, SignatureSafePaymentSection, Invoice },
  data() {
    return {
      name: null,
      paymentMethod: 'FREE',
      price: 0,
      status: 'pending',
      cycle: 'MONTHLY',
      creditCardFinalNumber: null,
      invoices: [],
      canClose: true,
    };
  },
  mounted() {
    this.getInvoices();
  },
  computed: {
    ...mapState('Payment', ['isLoading'])
  },
  methods: {
    ...mapActions('Payment', ['setLoading']),
    async getInvoices() {
      try {
        this.setLoading(true);
        const { data } = await OrganizationSignatureService.getInvoices();
        const { name, price, paymentMethod, status, creditCardFinalNumber, cycle } = data.signature;

        this.name = name;
        this.paymentMethod = paymentMethod;
        this.price = price;
        this.cycle = cycle;
        this.status = status;
        this.creditCardFinalNumber = creditCardFinalNumber;

        this.invoices = data.invoices;

        const hasOverdueInvoice = data.invoices.some((invoice) => invoice.status === 'OVERDUE');
        this.canClose = !hasOverdueInvoice;

      } finally {
        this.setLoading(false);
        const { organization } = this.$store.getters['Auth/user'];
        if (organization.organization_signatures?.[0].type === 'FREE') {
          const firstDay = new Date(`${organization.organization_signatures?.[0].date}T23:59:59.999Z`);
          const now = new Date();
          const diffInTime = Math.abs(now - firstDay);
          const diffInDays = Math.ceil(diffInTime / (1000 * 60 * 60 * 24));

          if (diffInDays >= 7) {
            this.canClose = false;
          };
        } else if (['pending', 'canceled'].includes(organization.organization_signatures?.[0].status)) {
          this.canClose = false;
        }
      }
    },
    handleClose() {
      this.$emit('close');
      this.name = null;
      this.paymentMethod = 'FREE';
      this.price = 0;
      this.status = 'pending';
      this.creditCardFinalNumber = null;
      this.invoices = [];
    },
    handleWhatsappClick(text) {
      const textToSend = text || 'Quero falar sobre um plano personalizado';
      window.open(`whatsapp://send?text=${textToSend}&phone=5565992774481`);
    },
    getOS() {
      const uA = navigator.userAgent || navigator.vendor || window.opera;
      if ((/iPad|iPhone|iPod/.test(uA) && !window.MSStream) || (uA.includes('Mac') && 'ontouchend' in document)) return 'iOS';
      return null;
    },
  }
};
</script>
<style lang="scss">
.current-signature {
  color: #6e7481;
  font-size: 12px;

  .current-plan {
    padding: 12px;
    background-color: #fff;
    border-radius: 8px;
  }

  .detail {
    display: flex;
    align-items: center;
    gap: 10px;
    color: #3f505f;
    font-size: 14px;
    font-weight: 600;

    p {
      flex-grow: 1;
      margin: 0;
    }

    span {
      font-size: 12px;
      font-weight: 400;
    }

    .icon {
      display: flex;
      align-items: center;
      justify-content: center;
      border-radius: 50%;
      background-color: #48b48d;
      width: 20px;
      height: 20px;
    }
  }

  .payment-method {
    display: flex;
    align-items: center;
    font-size: 14px;

    .method {
      display: flex;
      align-items: center;
      gap: 12px;
    }

    .description {
      color: #3f505f;
      font-size: 14px;
      font-weight: 600;
      letter-spacing: 0.4px;
    }
  }
}

.empty {
  padding: 50px 10px;
  text-align: center;
}
</style>
